import { getURL } from '../utils/resource';
import ENV from '../config/environment';
import FilterConstants from '../filter-constants';
import * as Format from '../utils/format';
import RSVP from 'rsvp';

let createCsvMultidimensionalData = function (headingsArray, dataArray, defaultValue) {
  let data = [];

  // Add the headings array as the first row.
  data.push(headingsArray);

  // Loop through each row in the each array (to the max length count).
  for (let i = 0; i < dataArray.length; i++) {
    let row = [];
    // Get the row data from the specified position in each array.
    let dataItem = dataArray[i];
    for (let j = 0; j < dataItem.length; j++) {
      // Add the value to the row if it's there, otherwise add an empty string or zero (defaultValue).
      row.push(dataItem[j] ? dataItem[j].toString() : defaultValue.toString());
    }
    // Add the row to the data.
    data.push(row);
  }
  return data;
};

///////////////////////////////////////////////////////////////////////////////
// design-files - functionality to collect design data for download          //
///////////////////////////////////////////////////////////////////////////////

export default class DesignFilesUtility {
  persistence = null;
  cdp = null;
  logger = null;
  intl = null;
  fastboot = null;
  assets = null;
  pdfFonts = [];

  constructor(cdp, persistence, logger, intl, fastboot, assets) {
    this.cdp = cdp;
    this.persistence = persistence;
    this.logger = logger;
    this.intl = intl;
    this.fastboot = fastboot;
    this.assets = assets;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Download Main Functions                                                    //
  ////////////////////////////////////////////////////////////////////////////////

  buildSavedDesign(inputs, tabs, name, desc, componentsAdvisor) {
    let filesToUpload = [];
    let saveData = this.persistence.getSaveData(tabs, inputs, componentsAdvisor).design;
    let now = new Date();
    let saveDateStamp =
      now.getFullYear() +
      '-' +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + now.getDate()).slice(-2) +
      ' ' +
      ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
      ':' +
      ('0' + now.getMinutes()).slice(-2) +
      ':' +
      ('0' + now.getSeconds()).slice(-2) +
      ' ' +
      (now.getHours() >= 12 ? 'PM' : 'AM');
    let version = '1.2';
    let toolName = 'FW';
    let designName = name && name.trim().length > 0 ? name : 'Filter Wizard Design';
    let designDesc = desc && desc.trim().length > 0 ? desc : 'Filter Wizard Design';
    let designData = {
      Name: designName,
      Description: designDesc,
      Tool: toolName,
      Version: version,
      Design: saveData,
      CreatedDate: saveDateStamp,
      UpdatedDate: saveDateStamp,
    };
    filesToUpload.push({ name: 'design.json', value: designData, location: '', type: 'DESIGNJSON' });
    return filesToUpload;
  }
  buildPDFData(model, images, name, desc) {
    let filesToUpload = [];
    let now = new Date();
    let saveDateStamp =
      ('0' + (now.getMonth() + 1)).slice(-2) + '/' + ('0' + now.getDate()).slice(-2) + '/' + now.getFullYear();
    let designName = name && name.trim().length > 0 ? name : 'Filter Wizard Design';
    let designDesc = desc && desc.trim().length > 0 ? desc : 'Filter Wizard Design';
    let dbUnit = this.intl.t('units.db').toString();
    let hzUnit = this.intl.t('units.hz').toString();
    const optimizationKeys = {
      Noise: 'panel.display-context.optimizations.noise',
      VoltageRange: 'panel.display-context.optimizations.voltage-range',
      Power: 'panel.display-context.optimizations.power',
    };
    let optimizationKey = optimizationKeys[model.inputs.components.optimization];
    if (FilterConstants.optimizationPreferenceOptions.specific === model.inputs.components.optimizationPreference) {
      optimizationKey = 'panel.display-context.optimizations.specific-components';
    }
    let componentChoices = this.intl
      .t('panel.display-context.components', {
        optimization: this.intl.t(optimizationKey).toString(),
        vplus: Format.formatFieldValue(model.inputs.components.vsPlus, FilterConstants.fieldTypes.NumericDecimal, '3s'),
        vminus: Format.formatFieldValue(
          model.inputs.components.vsMinus,
          FilterConstants.fieldTypes.NumericDecimal,
          '3s',
        ),
      })
      .toString();
    let pdfData = {
      dictionary: [
        { name: 'designDescription', value: this._generatePDFTitlePageDescription(designName, '', saveDateStamp) },
        { name: 'ieImageMissing', value: this.intl.t('download.pdf-content.pdf-data-ie-image-missing').toString() },
      ],
      applicationDisplayName: this.intl.t('download.pdf-content.pdf-tool-name').toString(),
      userName: ' ',
      heading: this.intl.t('download.pdf-content.pdf-heading').toString(),
      subheading: this.intl
        .t('download.pdf-content.pdf-subheading', {
          filterType: this.intl.t('panel.display-context.filter-type.' + model.inputs.filterType).toString(),
          nthOrder: this.intl
            .t('download.pdf-content.pdf-orders.order' + model.results.specifications.nthOrder)
            .toString(),
          filterResponse: this.intl
            .t('annotations.filterResponse.responseType.' + model.results.specifications.responseType)
            .toString(),
        })
        .toString(),
      fields: [
        { name: this.intl.t('wizard.specifications').toString(), value: componentChoices },
        {
          name: this.intl.t('settings.specifications.gain').toString(),
          value:
            Format.formatFieldValue(model.inputs.specifications.gain, FilterConstants.fieldTypes.NumericDecimal, '3s') +
            ' ' +
            model.inputs.specifications.gainUnit,
        },
        {
          name: this.intl.t('settings.specifications.passband').toString(),
          value:
            Format.formatFieldValue(
              model.inputs.specifications.passbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ) +
            dbUnit +
            ' at ' +
            Format.formatFieldValue(
              model.inputs.specifications.passbandFrequency,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ) +
            hzUnit,
        },
        {
          name: this.intl.t('settings.specifications.stopband').toString(),
          value:
            Format.formatFieldValue(
              model.inputs.specifications.stopbandAttenuation,
              FilterConstants.fieldTypes.NumericDecimal,
              '3s',
            ) +
            dbUnit +
            ' at ' +
            Format.formatFieldValue(
              model.inputs.specifications.stopbandFrequency,
              FilterConstants.fieldTypes.Engineering,
              '3s',
            ) +
            hzUnit,
        },
        {
          name: this.intl.t('settings.tolerances.component-tolerances').toString(),
          value:
            this.intl.t('settings.tolerances.capacitor').toString() +
            ' = ' +
            model.inputs.tolerances.capacitorTolerance +
            '; ' +
            this.intl.t('settings.tolerances.resistor').toString() +
            ' = ' +
            model.inputs.tolerances.resistorTolerance +
            '; ' +
            this.intl.t('settings.tolerances.inductor').toString() +
            ' = ' +
            model.inputs.tolerances.inductorTolerance +
            '; ' +
            this.intl.t('settings.tolerances.op-amp-gbw').toString() +
            ' = ' +
            model.inputs.tolerances.opAmpGbwTolerance,
        },
        { name: 'BOM', value: 'refer to BOM.csv file' },
      ],
      images: [
        {
          name: this.intl.t('download.pdf-content.image-headers.magnitude-db').toString(),
          value: this._cleanImage(images[0].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.magnitude-vv').toString(),
          value: this._cleanImage(images[1].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.phase-degrees').toString(),
          value: this._cleanImage(images[2].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.phase-radians').toString(),
          value: this._cleanImage(images[3].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.group-delay').toString(),
          value: this._cleanImage(images[4].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.phase-delay').toString(),
          value: this._cleanImage(images[5].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.step-response').toString(),
          value: this._cleanImage(images[6].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.input-impedance-mag').toString(),
          value: this._cleanImage(images[7].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.input-impedance-phase').toString(),
          value: this._cleanImage(images[8].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.noise').toString(),
          value: this._cleanImage(images[9].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.stages').toString(),
          value: this._cleanImage(images[10].data),
        },
        {
          name: this.intl.t('download.pdf-content.image-headers.circuit').toString(),
          value: this._cleanImage(images[11].data),
        },
      ],
      designInfo: { name: designName, description: designDesc, dataSheetUrl: null },
      date: saveDateStamp,
    };
    filesToUpload.push({ name: 'Design.pdf', value: pdfData, location: '', type: 'DESIGNPDF' });
    return filesToUpload;
  }
  buildPDFBinaryData(model, images, name, desc, designReport) {
    let filesToUpload = [];
    let now = new Date();
    let saveDateStamp =
      ('0' + (now.getMonth() + 1)).slice(-2) + '/' + ('0' + now.getDate()).slice(-2) + '/' + now.getFullYear();
    let designName = name && name.trim().length > 0 ? name : 'Filter Wizard Design';
    let designDesc = desc && desc.trim().length > 0 ? desc : '';
    let dbUnit = this.intl.t('units.db').toString();
    let hzUnit = this.intl.t('units.hz').toString();
    const optimizationKeys = {
      Noise: 'panel.display-context.optimizations.noise',
      VoltageRange: 'panel.display-context.optimizations.voltage-range',
      Power: 'panel.display-context.optimizations.power',
    };
    let optimizationKey = optimizationKeys[model.inputs.components.optimization];
    if (FilterConstants.optimizationPreferenceOptions.specific === model.inputs.components.optimizationPreference) {
      optimizationKey = 'panel.display-context.optimizations.specific-components';
    }
    let componentChoices = this.intl
      .t('panel.display-context.components', {
        optimization: this.intl.t(optimizationKey).toString(),
        vplus: Format.formatFieldValue(model.inputs.components.vsPlus, FilterConstants.fieldTypes.NumericDecimal, '3s'),
        vminus: Format.formatFieldValue(
          model.inputs.components.vsMinus,
          FilterConstants.fieldTypes.NumericDecimal,
          '3s',
        ),
      })
      .toString();
    let fields = [
      { name: this.intl.t('wizard.specifications').toString(), value: componentChoices },
      {
        name: this.intl.t('settings.specifications.gain').toString(),
        value:
          Format.formatFieldValue(model.inputs.specifications.gain, FilterConstants.fieldTypes.NumericDecimal, '3s') +
          ' ' +
          model.inputs.specifications.gainUnit,
      },
      {
        name: this.intl.t('settings.specifications.passband').toString(),
        value:
          Format.formatFieldValue(
            model.inputs.specifications.passbandAttenuation,
            FilterConstants.fieldTypes.NumericDecimal,
            '3s',
          ) +
          dbUnit +
          ' at ' +
          Format.formatFieldValue(
            model.inputs.specifications.passbandFrequency,
            FilterConstants.fieldTypes.Engineering,
            '3s',
          ) +
          hzUnit,
      },
      {
        name: this.intl.t('settings.specifications.stopband').toString(),
        value:
          Format.formatFieldValue(
            model.inputs.specifications.stopbandAttenuation,
            FilterConstants.fieldTypes.NumericDecimal,
            '3s',
          ) +
          dbUnit +
          ' at ' +
          Format.formatFieldValue(
            model.inputs.specifications.stopbandFrequency,
            FilterConstants.fieldTypes.Engineering,
            '3s',
          ) +
          hzUnit,
      },
      {
        name: this.intl.t('settings.tolerances.component-tolerances').toString(),
        value:
          this.intl.t('settings.tolerances.capacitor').toString() +
          ' = ' +
          model.inputs.tolerances.capacitorTolerance +
          '; ' +
          this.intl.t('settings.tolerances.resistor').toString() +
          ' = ' +
          model.inputs.tolerances.resistorTolerance +
          '; ' +
          this.intl.t('settings.tolerances.inductor').toString() +
          ' = ' +
          model.inputs.tolerances.inductorTolerance +
          '; ' +
          this.intl.t('settings.tolerances.op-amp-gbw').toString() +
          ' = ' +
          model.inputs.tolerances.opAmpGbwTolerance,
      },
      { name: 'BOM', value: 'refer to BOM.csv file' },
    ];
    let imagesList = [
      {
        name: this.intl.t('download.pdf-content.image-headers.magnitude-db').toString(),
        value: atob(this._cleanImage(images[0].data)),
        width: images[0].width,
        height: images[0].height,
        format: images[0].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.magnitude-vv').toString(),
        value: atob(this._cleanImage(images[1].data)),
        width: images[1].width,
        height: images[1].height,
        format: images[1].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.phase-degrees').toString(),
        value: atob(this._cleanImage(images[2].data)),
        width: images[2].width,
        height: images[2].height,
        format: images[2].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.phase-radians').toString(),
        value: atob(this._cleanImage(images[3].data)),
        width: images[3].width,
        height: images[3].height,
        format: images[3].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.group-delay').toString(),
        value: atob(this._cleanImage(images[4].data)),
        width: images[4].width,
        height: images[4].height,
        format: images[4].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.phase-delay').toString(),
        value: atob(this._cleanImage(images[5].data)),
        width: images[5].width,
        height: images[5].height,
        format: images[5].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.step-response').toString(),
        value: atob(this._cleanImage(images[6].data)),
        width: images[6].width,
        height: images[6].height,
        format: images[6].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.input-impedance-mag').toString(),
        value: atob(this._cleanImage(images[7].data)),
        width: images[7].width,
        height: images[7].height,
        format: images[7].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.input-impedance-phase').toString(),
        value: atob(this._cleanImage(images[8].data)),
        width: images[8].width,
        height: images[8].height,
        format: images[8].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.noise').toString(),
        value: atob(this._cleanImage(images[9].data)),
        width: images[9].width,
        height: images[9].height,
        format: images[9].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.stages').toString(),
        value: atob(this._cleanImage(images[10].data)),
        width: images[10].width,
        height: images[9].height,
        format: images[10].format,
      },
      {
        name: this.intl.t('download.pdf-content.image-headers.circuit').toString(),
        value: atob(this._cleanImage(images[11].data)),
        width: images[11].width,
        height: images[9].height,
        format: images[11].format,
      },
    ];

    return new RSVP.Promise((resolve, reject) => {
      let coverImage = getURL(ENV.rootURL + FilterConstants.coverImageResource, this.fastboot, this.logger);
      return this.assets
        .getAsset(coverImage, 'BLOB')
        .then((content) => {
          // 1. Read the Cover Image that will be used in the PDF
          return new RSVP.Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.readAsDataURL(content);
            reader.onloadend = function () {
              let coverImageData = reader.result;
              if (coverImageData !== null) {
                resolve(coverImageData);
              } else {
                reject(coverImageData);
              }
            };
          });
        })
        .then((coverImageData) => {
          // 2. Create new Design Report Object
          let pdfReport = new designReport(this.pdfFonts);

          // 3. Add Title Page
          pdfReport.addTitlePage(
            this.intl.t('download.pdf-content.pdf-tool-name').toString(),
            this._generatePDFTitlePageDescription(designName, designDesc, saveDateStamp),
            { data: atob(coverImageData.replace('data:image/jpeg;base64,', '')), format: 'JPEG' },
          );

          // 4. Add Data Page
          pdfReport.addDataPage(
            this.intl.t('download.pdf-content.pdf-heading').toString(),
            this.intl
              .t('download.pdf-content.pdf-subheading', {
                filterType: this.intl.t('panel.display-context.filter-type.' + model.inputs.filterType).toString(),
                nthOrder: this.intl
                  .t('download.pdf-content.pdf-orders.order' + model.results.specifications.nthOrder)
                  .toString(),
                filterResponse: this.intl
                  .t('annotations.filterResponse.responseType.' + model.results.specifications.responseType)
                  .toString(),
              })
              .toString(),
            fields,
          );

          // 5. Add Image Pages
          for (let i = 0; i < imagesList.length; i++) {
            pdfReport.addImagePage(imagesList[i]);
          }

          // Read Generated PDF File and convert to Base-64 String
          let reader = new FileReader();
          reader.readAsDataURL(pdfReport.toBlob());
          reader.onloadend = function () {
            let pdfData = reader.result;
            pdfData = pdfData.replace('data:application/pdf;base64,', '');
            if (pdfData !== null) {
              filesToUpload.push({ name: 'Design.pdf', value: pdfData, location: '', type: 'BINARY' });
              resolve(filesToUpload);
            } else {
              reject(filesToUpload);
            }
          };
        });
    });
  }
  buildBOMData(model) {
    let filesToUpload = [],
      bomDownloadData = [],
      bomData = model.billOfMaterials;
    for (let i = 0; i < bomData.length; i++) {
      let bomItem = Object.assign({}, bomData[i]);
      let value = '';
      if (bomItem.value !== '') {
        value = bomItem.value.replace('Ω', ' ohm'); // replace Ω with ohm
      }
      if (bomItem.package !== '') {
        let pkg = bomItem.package;
        // Change Package Details to Conform to Locale Strings on Extra Parts
        if (pkg.includes('{{header}}')) {
          pkg = pkg.replace('{{header}}', this.intl.t('figures.billOfMaterials.extra-components.header').toString());
        } else if (pkg.includes('{{socket}}')) {
          pkg = pkg.replace('{{socket}}', this.intl.t('figures.billOfMaterials.extra-components.socket').toString());
        } else if (pkg.includes('{{jumper}}')) {
          pkg = pkg.replace('{{jumper}}', this.intl.t('figures.billOfMaterials.extra-components.jumper').toString());
        } else if (pkg.includes('{{test-pin}}')) {
          pkg = pkg.replace(
            '{{test-pin}}',
            this.intl.t('figures.billOfMaterials.extra-components.test-pin').toString(),
          );
        } else if (pkg.includes('{{sma-jack}}')) {
          pkg = pkg.replace(
            '{{sma-jack}}',
            this.intl.t('figures.billOfMaterials.extra-components.sma-jack').toString(),
          );
        } else if (pkg.includes('{{sma-male}}')) {
          pkg = pkg.replace(
            '{{sma-male}}',
            this.intl.t('figures.billOfMaterials.extra-components.sma-male').toString(),
          );
        }
        value += ', ' + pkg;
      }
      if (bomItem.material !== '') {
        let material = bomItem.material;
        // Check for Materials if it needs translation
        if (material !== null) {
          if (material.includes('{{tantalum}}')) {
            material = material.replace(
              '{{tantalum}}',
              this.intl.t('figures.billOfMaterials.materials.tantalum').toString(),
            );
          }
        }
        value += ', ' + material;
      }
      if (bomItem.tolerance !== '') {
        value += ', ' + bomItem.tolerance;
      }
      bomDownloadData.push({ name: value, quantity: bomItem.count, identifier: bomItem.designators });
    }
    filesToUpload.push({ name: 'BOM.csv', value: bomDownloadData, location: '', type: 'BOM' });
    return filesToUpload;
  }
  buildGraphToCSVDataFiles(model) {
    let filesToUpload = [];
    let csvHeader = ['Frequency', 'Nominal', 'Min', 'Max'];

    // magnitudeDB
    filesToUpload.push({
      name: 'Magnitude(dB).csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.magnitudeDb.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // magnitudeVV
    filesToUpload.push({
      name: 'Magnitude(Volts per Volt).csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.magnitudeVV.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // inputImpedanceMag
    filesToUpload.push({
      name: 'Input Impedance Magnitude.csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.inputImpedanceMagnitude.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // inputImpedancePhase
    filesToUpload.push({
      name: 'Input Impedance Phase.csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.inputImpedancePhase.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // phaseRad
    filesToUpload.push({
      name: 'Phase(radians).csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.phaseRadians.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // phaseDegrees
    filesToUpload.push({
      name: 'Phase(degrees).csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.phaseDegrees.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // groupDelay
    filesToUpload.push({
      name: 'Group Delay.csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.groupDelay.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // phaseDelay
    filesToUpload.push({
      name: 'Phase Delay.csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.phaseDelay.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    // stepResponse
    filesToUpload.push({
      name: 'Step Response.csv',
      value: createCsvMultidimensionalData(
        ['Time', ...csvHeader.slice(1)],
        model.nextSteps.stepResponse.data.slice(0),
        0,
      ),
      location: 'Data Files',
      type: 'CSV',
    });

    //Noise
    filesToUpload.push({
      name: 'Noise.csv',
      value: createCsvMultidimensionalData(csvHeader, model.nextSteps.noise.graphData.data.slice(0), 0),
      location: 'Data Files',
      type: 'CSV',
    });

    return filesToUpload;
  }
  buildEvalBoardFiles(model, locale) {
    let filesToUpload = [];
    let evalBoardList = [];
    let evalBoardData = model.evalBoardData;
    for (let i = 0; i < evalBoardData.length; i++) {
      let evalBoardItem = Object.assign({}, evalBoardData[i]);
      if (evalBoardItem.evalBoardModelNumber !== null) {
        if (evalBoardList.length === 0 || evalBoardList.indexOf(evalBoardItem.evalBoardModelNumber) > -1 === false) {
          evalBoardList.push(evalBoardItem.evalBoardModelNumber);
        }
      }
    }
    // Add Eval Boards to the list of files to upload
    for (let j = 0; j < evalBoardList.length; j++) {
      let evalBoardURI = '/' + locale + '/designtools/filterwizard/EvalBoardFiles/' + evalBoardList[j] + '.zip';
      filesToUpload.push({
        name: evalBoardList[j] + '.zip',
        value: evalBoardURI,
        location: 'Eval Board Files',
        type: 'MEDIA',
      });
    }
    return filesToUpload;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Support Functions for building the PDF file                                //
  ////////////////////////////////////////////////////////////////////////////////

  _generatePDFTitlePageDescription(designName, userNameString, pdfDate) {
    let titlePageText = '';
    if (this._isNullOrWhiteSpace(designName) && this._isNullOrWhiteSpace(userNameString)) {
      // Username and design missing.
      titlePageText = this.intl.t('download.pdf-content.pdf-desc-no-name-desc', { date: pdfDate }).toString();
    } else if (this._isNullOrWhiteSpace(designName) && !this._isNullOrWhiteSpace(userNameString)) {
      // Design name missing and username is present.
      titlePageText = this.intl
        .t('download.pdf-content.pdf-desc-no-design-name', { user: userNameString.trim(), date: pdfDate })
        .toString();
    } else if (this._isNullOrWhiteSpace(userNameString) && !this._isNullOrWhiteSpace(designName)) {
      // Username name missing and design name is present.
      titlePageText = this.intl
        .t('download.pdf-content.pdf-desc-no-user-name', { desc: designName.trim(), date: pdfDate })
        .toString();
    } else {
      // Username and design presnt.
      titlePageText = this.intl
        .t('download.pdf-content.pdf-desc', { desc: designName.trim(), user: userNameString.trim(), date: pdfDate })
        .toString();
    }
    return titlePageText;
  }
  _isNullOrWhiteSpace(str) {
    // Check if the string is undefined.
    if (str)
      // Check if the string is empty, null or white space.
      return str === null || str.match(/^ *$/) !== null;
    else return true;
  }
  _cleanImage(image) {
    let stringToRemove = 'data:image/png;base64,';
    let imageString = image.toString();
    imageString = imageString.replace(stringToRemove, '');
    return imageString;
  }
}
